import React, { useState } from 'react';
import T from 'Components/Helpers/translate';
import { Icon, Loader, Button } from '@upsales/components';
import Prospecting, { TIER_DECLINED_ERROR } from 'App/babel/resources/Prospecting';
import { getCountryFromProspectingId } from 'App/services/prospectingService';
import logError from 'App/babel/helpers/logError';
import StateRouter from 'App/services/StateRouter';

export type ProspectingSearchResult = {
	name: string;
	prospectingId: string;
	orgNumber: string;
	visitingTown?: string | null;
	postTown?: string | null;
	registeredTown?: string | null;
};

type SupersearchProspectingResultsProps = {
	results: ProspectingSearchResult[];
	loading: boolean;
	error: boolean;
	onCompanyAdded: () => void;
};

/** The section's heading; Lookup's row sits under it too (WEB-13132). */
export const SupersearchProspectingHeader = () => (
	<div className="supersearch-prospecting__header">
		<Icon name="search" />
		<span>{T('navbar.quickSearch.prospecting.title')}</span>
	</div>
);

const SupersearchProspectingResults = ({
	results,
	loading,
	error,
	onCompanyAdded
}: SupersearchProspectingResultsProps) => {
	const [savingIds, setSavingIds] = useState<Record<string, boolean>>({});

	const addToCRM = async (result: ProspectingSearchResult) => {
		const { prospectingId } = result;
		setSavingIds(prev => ({ ...prev, [prospectingId]: true }));
		try {
			const saved = await Prospecting.save({ prospectingId });
			const newId = saved?.data?.id;
			onCompanyAdded();
			if (newId) {
				StateRouter.go('account.dashboard', { id: newId });
			}
		} catch (e) {
			if (e === TIER_DECLINED_ERROR) {
				return;
			}
			logError(e, 'Failed to add prospecting company from supersearch');
		} finally {
			setSavingIds(prev => ({ ...prev, [prospectingId]: false }));
		}
	};

	const header = <SupersearchProspectingHeader />;

	if (loading) {
		return (
			<div className="supersearch-prospecting">
				{header}
				<div className="supersearch-prospecting__loader">
					<Loader size="sm" />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="supersearch-prospecting">
				{header}
				<div className="supersearch-prospecting__empty">{T('navbar.quickSearch.prospecting.error')}</div>
			</div>
		);
	}

	if (!results.length) {
		return (
			<div className="supersearch-prospecting">
				{header}
				<div className="supersearch-prospecting__empty">{T('navbar.quickSearch.prospecting.noResults')}</div>
			</div>
		);
	}

	return (
		<div className="supersearch-prospecting">
			{header}
			<div className="supersearch-prospecting__results">
				{results.map(result => {
					const country = getCountryFromProspectingId(result.prospectingId);
					const city = result.visitingTown || result.postTown || result.registeredTown;
					const isSaving = savingIds[result.prospectingId];

					return (
						<div key={result.prospectingId} className="supersearch-prospecting__card">
							<div className="supersearch-prospecting__card-info">
								<div className="supersearch-prospecting__card-name">{result.name}</div>
								<div className="supersearch-prospecting__card-details">
									<span className="supersearch-prospecting__card-source">
										{country ? (
											<b className={'flag-icon flag-icon-' + country.toLowerCase()} />
										) : null}
										{T('default.prospecting')}
									</span>
									{city ? (
										<span>
											{' | '}
											{city}
										</span>
									) : null}
									{result.orgNumber ? (
										<span>
											{' | '}
											{T('default.orgNumber')}: {result.orgNumber}
										</span>
									) : null}
								</div>
							</div>
							<div className="supersearch-prospecting__card-action">
								<Button
									type="lined"
									color="green"
									size="sm"
									loading={isSaving}
									onClick={(e: React.MouseEvent) => {
										e.stopPropagation();
										addToCRM(result);
									}}
								>
									{'+ ' + T('navbar.quickSearch.prospecting.addToCRM')}
								</Button>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default SupersearchProspectingResults;
